import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiJson, ApiError, orgPath } from "../api/client";
import { useAuth } from "../auth/useAuth";

/**
 * Wraps `useQuery` for the project's most common case: a GET to an org-scoped
 * API path that should only fire once a current org is resolved.
 *
 * - `baseKey` is the caller's cache prefix (e.g. `['admin-things']`). The hook
 *   appends the active org slug so each org has its own entry.
 * - `path` is passed straight to `orgPath()` (e.g. `'/admin/things'`).
 * - `enabled: false` from the caller disables the query unconditionally.
 *   Omit or `true` to let only the slug gate matter.
 */
export function useOrgQuery<TData, TSelected = TData>(
  baseKey: readonly unknown[],
  path: string,
  options?: Omit<
    UseQueryOptions<TData, ApiError, TSelected, readonly unknown[]>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<TSelected, ApiError> {
  const { currentOrgSlug } = useAuth();
  return useQuery<TData, ApiError, TSelected, readonly unknown[]>({
    ...options,
    queryKey: [...baseKey, currentOrgSlug],
    queryFn: () => apiJson<TData>(orgPath(path)),
    enabled: !!currentOrgSlug && options?.enabled !== false,
  });
}
